import { useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { Container, Grid, IconButton, TextField, Tooltip } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { MdAdd, MdFileDownload } from "react-icons/md";
import Button from "../shared/Button";

import { useAppDispatch, useAppSelector } from "../../hooks";
import { history } from "../..";
import {
  clearTransportCompany,
  clearTransportCompanyContacts,
  clearTransportCompanyTrucks,
  getTransportCompanies,
  getTrucks,
  setTransportCompanies,
  setTrucks,
} from "../../store/transportStore";
import transportCompanyService from "../../services/transportCompanyService";
import { TransportCompany } from "../../interface/api/TransportCompany";
import { SetTransportCompaniesAction } from "../../interface/redux-types/SetTransportCompaniesAction";
import PageHero from "../shared/page-hero";
import DeleteDialog from "../shared/dialog/delete";
import useTimeout from "../../hooks/useTimeout";
import { ActionIconGroup, DeleteIcon, EditIcon, MoveIcon } from "../shared/icons";
import MoveTrucksDialog from "../shared/dialog/move-trucks";
import truckService from "../../services/truckService";
import { MoveTrucksPayload } from "../../interface/api/MoveTrucksPayload";
import { toast } from "react-toastify";
import { Truck } from "../../interface/api/Truck";
import { SetTrucksAction } from "../../interface/redux-types/SetTrucksAction";
import { TransportPayload } from "../../interface/api/TransportPayload";
import DataTable, { TableColumn } from "react-data-table-component";
import { tableCustomStyles } from "../../utils/table-styles";
import PaginatedTableTop, { paginationTopClassName } from "../shared/paginated-table-top";
import * as FileSaver from "file-saver";
import * as XLSX from "sheetjs-style";

const useStyles = makeStyles({
  table: {
    minWidth: 500,
  },
  addIconButton: {
    border: "1px solid rgba(0, 0, 0, 0.23)",
  },
});

const TransportsView = ({}) => {
  const classes = useStyles();
  const transportCompanies = useAppSelector(getTransportCompanies);
  const trucks = useAppSelector(getTrucks);
  const dispatch = useAppDispatch();
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openMoveTrucksDialog, setOpenMoveTrucksDialog] = useState(false);
  const [selectedItem, setSelectedItem] = useState<Partial<TransportCompany>>(
    {}
  );
  const [exporting, setExporting] = useState(false);
  const [searchText, setSearchText] = useState("");

  const filteredCompanies = useMemo(() => {
    if (!searchText) return transportCompanies;
    const lower = searchText.toLowerCase();
    return transportCompanies.filter((c) =>
      c.name.toLowerCase().includes(lower)
    );
  }, [transportCompanies, searchText]);

  useTimeout(() => dispatch(clearTransportCompany()), 100);

  useEffect(() => {
    return () => {
      dispatch(clearTransportCompanyContacts());
      dispatch(clearTransportCompanyTrucks());
    };
  }, []);

  useEffect(() => {
    transportCompanyService.getTransportCompanies().then((response) => {
      if (response.ok) {
        const transportCompanies = response.data as TransportCompany[];
        const setTransportCompaniesAction: SetTransportCompaniesAction = {
          transportCompanies,
        };
        dispatch(setTransportCompanies(setTransportCompaniesAction));
      }
    });

    const transportPayload: TransportPayload = {
      transportCompanyId: null,
      truck: null,
      transportContact: null,
    };

    truckService.getTrucks(transportPayload).then((response) => {
      if (response.ok) {
        const trucks = response.data as Truck[];
        const setTrucksAction: SetTrucksAction = {
          trucks,
        };
        dispatch(setTrucks(setTrucksAction));
      }
    });
  }, []);

  const onConfirmDelete = () => {
    if (selectedItem && selectedItem.id) {
      transportCompanyService
        .deleteTransportCompany(selectedItem.id)
        .then((response) => {
          if (response.ok) {
            const setTransportCompaniesAction: SetTransportCompaniesAction = {
              transportCompanies: transportCompanies.filter(
                (t) => t.id !== selectedItem.id
              ),
            };
            dispatch(setTransportCompanies(setTransportCompaniesAction));
          }
        });
    }

    setOpenDeleteDialog(false);
  };

  const onMoveTrucks = (transportCompanyId: number) => {
    if (!selectedItem?.id) return;

    const moveTrucksPayload: MoveTrucksPayload = {
      fromTransportCompanyId: selectedItem.id,
      toTransportCompanyId: transportCompanyId,
    };

    truckService.moveTrucks(moveTrucksPayload).then((response) => {
      if (!response.ok) {
        toast.error("Failed to move trucks");
      }
    });

    setOpenMoveTrucksDialog(false);
  };

  const onExport = () => {
    setExporting(true);

    const ws_data: any[][] = [];

    // Header block
    ws_data.push(["UNITED SOILS MANAGEMENT LTD"]);
    ws_data.push(["Transport Company Export"]);
    ws_data.push([
      `Date: ${new Date().toLocaleDateString("en-US", {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
      })}`,
    ]);
    ws_data.push([]);

    const headers = [
      "transportCompanyName",
      "truckNumber",
      "licensePlate",
      "driverName",
      "driverNumber",
      "tareWeight",
    ];
    ws_data.push(headers);

    const exportRows: any[][] = [];
    const merges: XLSX.Range[] = [];

    let totalTrucks = 0;
    let rowOffset = ws_data.length;

    transportCompanies.forEach((company) => {
      const trucksForCompany = trucks.filter(
        (t) => t.transportCompanyId === company.id
      );
      totalTrucks += trucksForCompany.length;

      const startRow = rowOffset + exportRows.length;

      if (trucksForCompany.length === 0) {
        exportRows.push([company.name, "", "", "", "", ""]);
      } else {
        trucksForCompany.forEach((truck) => {
          exportRows.push([
            company.name,
            truck.number || "",
            truck.licensePlate || "",
            truck.driverName || "",
            truck.driverNumber || "",
            truck.tareWeight || "",
          ]);
        });

        const endRow = startRow + trucksForCompany.length - 1;

        if (trucksForCompany.length > 1) {
          merges.push({
            s: { r: startRow, c: 0 },
            e: { r: endRow, c: 0 },
          });
        }

        // Add merged total row only (no extra blank row)
        const totalRowIndex = rowOffset + exportRows.length;
        exportRows.push([
          `Total Trucks for ${company.name}`,
          "",
          "",
          "",
          "",
          trucksForCompany.length,
        ]);
        merges.push({
          s: { r: totalRowIndex, c: 0 },
          e: { r: totalRowIndex, c: 4 },
        });
      }
    });

    // Final total row
    const totalAllRowIdx = rowOffset + exportRows.length;
    exportRows.push(["Total All Trucks", "", "", "", "", totalTrucks]);
    merges.push({
      s: { r: totalAllRowIdx, c: 0 },
      e: { r: totalAllRowIdx, c: 4 },
    });

    const totalCompaniesRowIdx = totalAllRowIdx + 1;
    exportRows.push([
      "Total Transport Companies",
      "",
      "",
      "",
      "",
      transportCompanies.length,
    ]);
    merges.push({
      s: { r: totalCompaniesRowIdx, c: 0 },
      e: { r: totalCompaniesRowIdx, c: 4 },
    });

    const fullData = [...ws_data, ...exportRows];
    const ws = XLSX.utils.aoa_to_sheet(fullData);

    // Header merges
    ws["!merges"] = [
      { s: { r: 0, c: 0 }, e: { r: 0, c: 5 } },
      { s: { r: 1, c: 0 }, e: { r: 1, c: 5 } },
      { s: { r: 2, c: 0 }, e: { r: 2, c: 5 } },
      ...merges,
    ];

    const border = {
      top: { style: "thin", color: { rgb: "000000" } },
      bottom: { style: "thin", color: { rgb: "000000" } },
      left: { style: "thin", color: { rgb: "000000" } },
      right: { style: "thin", color: { rgb: "000000" } },
    };

    const range = XLSX.utils.decode_range(ws["!ref"] || "");
    for (let R = 0; R <= range.e.r; ++R) {
      for (let C = 0; C <= range.e.c; ++C) {
        const cellRef = XLSX.utils.encode_cell({ r: R, c: C });
        if (!ws[cellRef]) continue;

        // Title area
        if (R <= 2) {
          ws[cellRef].s = {
            font: { bold: true, sz: 14 },
            alignment: { horizontal: "center", vertical: "center" },
          };
        }
        // Header row
        else if (R === 4) {
          ws[cellRef].s = {
            font: { bold: true, sz: 16, color: { rgb: "FFFFFF" } },
            fill: { patternType: "solid", fgColor: { rgb: "4F4F4F" } },
            alignment: { horizontal: "center", vertical: "center" },
            border,
          };
        }
        // Totals (highlight + bold)
        else if (
          fullData[R][0]?.toString().startsWith("Total Trucks") ||
          fullData[R][0]?.toString().startsWith("Total All") ||
          fullData[R][0]?.toString().startsWith("Total Transport")
        ) {
          ws[cellRef].s = {
            font: { bold: true },
            fill: { patternType: "solid", fgColor: { rgb: "D9D9D9" } },
            alignment: { horizontal: "left", vertical: "center" },
            border,
          };
        }
        // Normal data
        else {
          ws[cellRef].s = {
            font: { sz: 11 },
            alignment: { horizontal: "left", vertical: "center" },
            border,
          };
        }
      }
    }

    // Auto column widths
    ws["!cols"] = headers.map((_, i) => {
      const maxLen = Math.max(
        ...fullData.map((row) => (row[i] ? String(row[i]).length : 0))
      );
      return { wch: maxLen + 2 };
    });

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Transport Company Export");

    const buffer = XLSX.write(wb, {
      bookType: "xlsx",
      type: "array",
      cellStyles: true,
    });

    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    FileSaver.saveAs(
      blob,
      `transport-company-export-${new Date().toISOString().split("T")[0]}.xlsx`
    );

    setTimeout(() => setExporting(false), 1500);
  };

  const columns: TableColumn<TransportCompany>[] = [
    {
      name: "ID",
      selector: (row) => row.id,
      sortable: true,
      width: "100px",
    },
    {
      name: "Name",
      selector: (row) => row.name,
      sortable: true,
    },
    {
      name: "Actions",
      cell: (row) => (
        <ActionIconGroup spread>
          <MoveIcon
            onClick={() => {
              setSelectedItem(row);
              setOpenMoveTrucksDialog(true);
            }}
          />
          <EditIcon
            onClick={() => history.push(`/transport-company/${row.id}`)}
          />
          <DeleteIcon
            onClick={() => {
              setSelectedItem(row);
              setOpenDeleteDialog(true);
            }}
          />
        </ActionIconGroup>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      width: "220px",
    },
  ];

  return (
    <>
      <Container maxWidth="xl">
        <PageHero
          title="Transports"
          subtitle="Add and manage Transport Companies"
          showBackButton={false}
          action={
            <>
              <Tooltip title="Export Transport Companies">
                <IconButton
                  className={classes.addIconButton}
                  onClick={() => onExport()}
                  disabled={exporting}
                >
                  <MdFileDownload size={24} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Add Transport Company">
                <IconButton
                  color="primary"
                  className={classes.addIconButton}
                  style={{ marginLeft: 8 }}
                  onClick={() => history.push(`/transport-company/new`)}
                >
                  <MdAdd size={24} />
                </IconButton>
              </Tooltip>
            </>
          }
        />
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4}>
            <TextField
              type="text"
              variant="outlined"
              fullWidth
              label="Search by Name"
              name="name"
              onChange={(e) => setSearchText(e.target.value)}
              value={searchText}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Button
              type="button"
              onClick={() => setSearchText("")}
              variant="contained"
              color="default"
              label="Clear"
            />
          </Grid>
        </Grid>
        <Grid container spacing={3}>
          <Grid item xs>
            {transportCompanies && (
              <PaginatedTableTop>
                <DataTable
                  customStyles={tableCustomStyles}
                  className={paginationTopClassName}
                  fixedHeader
                  striped
                  columns={columns}
                  data={filteredCompanies}
                  pagination
                  paginationPerPage={50}
                  paginationRowsPerPageOptions={[50, 100, 150]}
                  highlightOnHover
                  responsive
                />
              </PaginatedTableTop>
            )}
          </Grid>
        </Grid>
      </Container>
      {openDeleteDialog && (
        <DeleteDialog
          open={openDeleteDialog}
          onClose={() => setOpenDeleteDialog(false)}
          onConfirmDelete={onConfirmDelete}
        />
      )}
      {openMoveTrucksDialog && (
        <MoveTrucksDialog
          open={openMoveTrucksDialog}
          transportCompanies={transportCompanies}
          onClose={() => setOpenMoveTrucksDialog(false)}
          onConfirm={onMoveTrucks}
          fromTransportCompanyName={selectedItem.name ?? ""}
        />
      )}
    </>
  );
};

export default connect()(TransportsView);
